import React, { useState, useRef, useEffect, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { format } from "date-fns";

import LottieIcon from "../../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../../assets/animations/checkBox.json";
import settingsAnimation from "../../../assets/animations/settings.json";
import { selectAutoPlay } from "../../../store/slices/common/slice";
import { useSelector } from "react-redux";

import {
  formatModelName,
  getPosterUrl as getBestPosterUrl,
  isImageOutput,
} from "../../../utils/utils";
import { COLLECTION_STATUS, MEDIA_TYPE } from "../../../utils/constants";
import {
  regenerateCollection,
  regenerateImageCollection,
} from "../../../store/slices/model/asyncThunks";

import {
  Date as DateLabel,
  ModelName,
  VideoElement,
  VideoFooter,
  CollectionContainer,
  ImageElement,
  CheckboxContainer,
  SkeletonDateChip,
  SkeletonNameBar,
  SkeletonOverlay,
} from "./CollectionItem.styles";
import { FailedState, InProgressState } from "./CollectionStatusStates";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/* Local thin wrapper. Uses the shared util that prefers
   output.video.posterUrl (dedicated first-frame still) over the input
   thumbnail, with input.image fallback. */
const getPosterUrl = (video) => getBestPosterUrl(video);

const getVideoUrl = (video) =>
  video?.output?.video?.compressedUrl || video?.output?.video?.url;

const getAspectRatio = (video) => {
  const w = video?.input?.image?.resizedImage?.width ?? 1;
  const h = video?.input?.image?.resizedImage?.height ?? 1.5;
  return w / h;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SettingsOverlay = ({ openEditTrainModel }) => (
  <CheckboxContainer
    onClick={(e) => {
      e.stopPropagation();
      openEditTrainModel?.();
    }}
  >
    <LottieIcon
      animationData={settingsAnimation}
      trigger="hover"
      strokeColor="#ffffff"
      size={28}
    />
  </CheckboxContainer>
);

const SelectOverlay = ({ isSelected }) => (
  <CheckboxContainer>
    <LottieIcon
      animationData={checkboxAnimation}
      speed={2}
      trigger="toggle-reverse"
      isActive={isSelected}
      size={24}
      strokeColor="#ffffff"
    />
  </CheckboxContainer>
);

// const MediaBadge = ({ isImage }) => (
//   <MediaBadgeWrapper>
//     {isImage ? <ImageIcon /> : <VideoRecorderIcon />}
//     <MediaBadgeText>{isImage ? "IMAGE" : "VIDEO"}</MediaBadgeText>
//   </MediaBadgeWrapper>
// );

const CompletedImage = ({ url, onLoad }) => (
  <ImageElement src={url} onLoad={onLoad} />
);

const CompletedVideo = ({ video, videoRef, activeSrc, onMediaReady }) => {
  const posterUrl = getPosterUrl(video);

  return (
    <VideoElement
      ref={videoRef}
      src={activeSrc}
      imageSrc={posterUrl}
      controls={false}
      preload="metadata"
      onMediaReady={onMediaReady}
    />
  );
};

const Footer = ({ video }) => (
  <VideoFooter>
    <ModelName>{formatModelName(video.name)}</ModelName>
    <DateLabel>{format(video.createdAt, "MM/dd/yyyy, h:mm a")}</DateLabel>
  </VideoFooter>
);

// ─── Main component ──────────────────────────────────────────────────────────

const CollectionItem = ({
  video,
  select,
  setSelectedCollections,
  selectedCollections,
  onCollectionItemClick,
  trainPage,
  openEditTrainModel,
}) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);

  const autoplayEnabled = useSelector(selectAutoPlay);

  const { ref: autoplayRef, inView: isInAutoplayView } = useInView({
    threshold: 0.7,
    rootMargin: "0px",
  });

  /* nearRef drives src attach/detach. Keep the band modest so grid scroll
     never accumulates a page-worth of active video decoders on mobile. */
  const { ref: nearRef, inView: isNear } = useInView({
    threshold: 0,
    rootMargin: "500px 0px",
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);

  /* Reset on item swap (virtualization could recycle this slot). */
  useEffect(() => {
    setIsImageLoaded(false);
    setIsVideoReady(false);
  }, [video?._id]);

  // ── Derived state ──

  const isFailed = video.status === COLLECTION_STATUS.FAILED;
  const inProgress = video.status !== COLLECTION_STATUS.COMPLETED && !isFailed;
  const isImage = video?.output?.type === MEDIA_TYPE.IMAGE;
  const videoUrl = getVideoUrl(video);
  const isContentReady = isImage ? isImageLoaded : Boolean(videoUrl);
  const isSelected = selectedCollections?.includes(video._id);
  const isClickable = (!inProgress && isContentReady) || select || isFailed;

  // ── Effects ──

  /* Same media attach rule as ExploreItem: keep the poster mounted, attach
     video src only while near enough and actively needed. */
  const activeOutputUrl =
    isNear &&
    !isImage &&
    !inProgress &&
    !isFailed &&
    videoUrl &&
    (autoplayEnabled ? isInAutoplayView : isHovered)
      ? videoUrl
      : null;

  // ── Handlers ──

  const handleRetry = useCallback(
    (e) => {
      e.stopPropagation();
      if (retryLoading) return;
      setRetryLoading(true);

      const seed = Math.floor(Math.random() * 10000000) + 1;
      const action = isImageOutput(video.modelType)
        ? regenerateImageCollection({ collectionId: video._id, seed })
        : regenerateCollection({ collectionId: video._id, seed });

      dispatch(action)
        .unwrap()
        .then(() => {
          toast.success("Regeneration started...");

          setRetryLoading(false);
        })
        .catch((err) => {
          toast.error(err);
          setRetryLoading(false);
        });
    },
    [dispatch, retryLoading, video._id, video.modelType],
  );

  const handleClick = useCallback(() => {
    if (select && !inProgress) {
      setSelectedCollections((prev) =>
        prev.includes(video._id)
          ? prev.filter((id) => id !== video._id)
          : [...prev, video._id],
      );
      return;
    }

    if (isContentReady && !inProgress && onCollectionItemClick) {
      onCollectionItemClick(video);
    }
  }, [
    select,
    isContentReady,
    inProgress,
    video,
    onCollectionItemClick,
    setSelectedCollections,
  ]);

  const ensureInlinePlayback = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;

    element.muted = true;
    element.defaultMuted = true;
    element.playsInline = true;
    element.loop = true;
    element.preload = "none";
    element.setAttribute("muted", "");
    element.setAttribute("playsinline", "");
    element.setAttribute("webkit-playsinline", "");
    element.setAttribute("loop", "");
  }, []);

  useEffect(() => {
    if (!videoRef.current || isImage) return;

    ensureInlinePlayback();

    const shouldPlay =
      !!activeOutputUrl && (autoplayEnabled ? isInAutoplayView : isHovered);

    if (shouldPlay) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      videoRef.current.pause();
    }
  }, [
    ensureInlinePlayback,
    autoplayEnabled,
    isInAutoplayView,
    isHovered,
    isImage,
    activeOutputUrl,
  ]);

  // ── Render ──

  return (
    <CollectionContainer
      ref={(el) => {
        autoplayRef(el);
        nearRef(el);
      }}
      $aspectRatio={getAspectRatio(video)}
      $clickable={isClickable}
      $inProgress={inProgress}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {trainPage && <SettingsOverlay openEditTrainModel={openEditTrainModel} />}
      {!inProgress && select && <SelectOverlay isSelected={isSelected} />}

      {/* <MediaBadge isImage={isImage} /> */}

      {!inProgress && !isFailed && isImage && (
        <CompletedImage
          url={video.output.image.url}
          onLoad={() => setIsImageLoaded(true)}
        />
      )}

      {!inProgress && !isFailed && !isImage && (
        <CompletedVideo
          video={video}
          videoRef={videoRef}
          activeSrc={activeOutputUrl}
          onMediaReady={() => setIsVideoReady(true)}
        />
      )}

      {/* Structured placeholder skeleton — mirrors the card's bottom
          footer (model name + date). Drops away once the underlying media
          (image bytes / poster / first video frame) lands. The card's
          existing water gradient covers the main area; only the footer
          placeholders pulse between two close shades. */}
      {!inProgress &&
        !isFailed &&
        (isImage ? !isImageLoaded : !isVideoReady) && (
          <SkeletonOverlay aria-hidden>
            <SkeletonNameBar />
            <SkeletonDateChip />
          </SkeletonOverlay>
        )}

      {isFailed && (
        <FailedState
          video={video}
          isImage={isImage}
          retryLoading={retryLoading}
          onRetry={handleRetry}
        />
      )}

      {inProgress && <InProgressState video={video} isImage={isImage} />}

      {!inProgress &&
        !isFailed &&
        (isImage ? isImageLoaded : isVideoReady) && <Footer video={video} />}
    </CollectionContainer>
  );
};

export default React.memo(CollectionItem);
